import React, { useState, useEffect, useMemo, FormEvent } from 'react'
import { Stack, Paper, Collapse } from '@mui/material'
import { entityTextField } from '../../../Utils/EntityField'
import { ClientDetailState } from '../../../../reducers/client/clientDetail'
import { ClientInput } from '../../../../models/Client'
import { useTranslation } from 'react-i18next'
import { ISTableTitle } from '../../../Utils/ISTable'
import { ArrowDropDownOutlined, ArrowDropUpOutlined, ContactsOutlined, InfoOutlined, PlaceOutlined } from '@mui/icons-material';
import { useThemeContext } from '../../../../theme/ThemeContext'
import { PersonListState } from '../../../../reducers/person/personList'
import People from '../../People'
import { clientStatusToDescription } from '..'
import { ISTextField } from '../../../Utils/ISTextField'
import { DeliveryLocationListState } from '../../../../reducers/deliveryLocation/deliveryLocationList'
import DeliveryLocations from '../../../Client/DeliveryLocation'
import { ProvinceListState } from '../../../../reducers/province/provinceList'

interface Props {
  client: ClientDetailState
  personList?: PersonListState
  deliveryLocationList: DeliveryLocationListState
  provinceList: ProvinceListState
}

const empty: ClientInput = {
  codigo_cliente: 0,
  nombre: '',
  cuit: '',
  nombrefantasia: '',
  domicilio: '',
  localidad: '',
  codigopostal: '',
  telefono: '',
  codigo_provincia: 0,
  estado: 0,
  codigo_vendedor: 0,
  codigo_zona: 0,
  codigo_pais: 0,
  codigo_transporte: 0,
  codigo_condicion_de_pago_ventas: 0
}

export const ClientInformation: React.FC<Props> = (props) => {
  const [, setChanged] = useState<boolean>(false)
  const [showFullInfo, setShowFullInfo] = useState<boolean>(false)
  const [personalInformation, setPersonalInformation] = useState<ClientInput>(props.client.client || empty)
  const [showPeople, setShowPeople] = useState<boolean>(false)
  const [showDeliveryLocation, setShowDeliveryLocation] = useState<boolean>(false)
  const { t } = useTranslation('client')
  const { theme } = useThemeContext()

  const change = (newPersonalInformation: ClientInput) => {
    setPersonalInformation(newPersonalInformation)
    setChanged(true)
  }

  useEffect(() => {
    if (props.client.client)
      setPersonalInformation(props.client.client)
  }, [props.client.client])

  const ClientInformationField = useMemo(() => entityTextField(change), [])

  return (

    <Paper style={{ borderRadius: "10px", margin: "10px", marginBottom: "20px" }} onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); setShowFullInfo(!showFullInfo) }}>
      <ISTableTitle sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }} title={`${t('info')} ${props.client.client?.nombre}`}>
        <Stack sx={{ display: "flex", flexDirection: "row" }}>
          <ContactsOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} />
          {showFullInfo ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> :
            <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} />}
        </Stack>
      </ISTableTitle>
      <Collapse style={{ padding: "10px" }} in={showFullInfo}>
        <Stack display="flex" flexDirection="row" flexGrow={1} >
          <ClientInformationField
            key="code"
            variant="outlined"
            label={t("code")}
            entity={personalInformation}
            attr="codigo_cliente"
            size="small"
            style={{ maxWidth: "100px", marginRight: "10px" }}
            disabled
          />
          <ClientInformationField
            key="name"
            variant="outlined"
            label={t("name")}
            entity={personalInformation}
            attr="nombre"
            size="small"
            disabled
          />
        </Stack>
        <Stack display="flex" flexDirection="row" flexGrow={1} >
          <ClientInformationField
            key="documentId"
            variant="outlined"
            label={t("documentId")}
            entity={personalInformation}
            attr="cuit"
            size="small"
            style={{ maxWidth: "150px", marginRight: "10px" }}
            disabled
          />
          <ClientInformationField
            key="fantasyName"
            variant="outlined"
            label={t("fantasyName")}
            entity={personalInformation}
            attr="nombrefantasia"
            size="small"
            disabled
          />
        </Stack>
        <Stack display="flex" flexDirection="row" flexGrow={1} >
          <ClientInformationField
            key="address"
            variant="outlined"
            label={t("address")}
            entity={personalInformation}
            attr="domicilio"
            style={{ marginRight: "10px" }}
            size="small"
            disabled
          />
          <ClientInformationField
            key="phoneNumber"
            variant="outlined"
            label={t("phoneNumber")}
            entity={personalInformation}
            attr="telefono"
            size="small"
            style={{ maxWidth: "350px" }}
            disabled
          />
        </Stack>
        <Stack display="flex" flexDirection="row" flexGrow={1} >
          <ClientInformationField
            key="zipCode"
            variant="outlined"
            label={t("zipCode")}
            entity={personalInformation}
            attr="codigopostal"
            size="small"
            style={{ maxWidth: "150px", marginRight: "10px" }}
            disabled
          />
          <ClientInformationField
            key="locality"
            variant="outlined"
            label={t("locality")}
            entity={personalInformation}
            attr="localidad"
            size="small"
            disabled
          />
          <ISTextField
            key="status"
            variant="outlined"
            label={t("status")}
            value={t(clientStatusToDescription(personalInformation.estado.toString()))}
            style={{ maxWidth: "200px", marginLeft: "10px" }}
            size="small"
            disabled
          />
        </Stack>
        <Paper style={{ borderRadius: "10px", marginTop: "10px", marginBottom: "10px" }} onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); setShowPeople(!showPeople) }}>
          <ISTableTitle sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }} title={t('users')}>
            <Stack sx={{ display: "flex", flexDirection: "row" }}>
              <InfoOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} />
              {showPeople ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> :
                <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} />}
            </Stack>
          </ISTableTitle>
          <Collapse style={{ padding: "10px" }} in={showPeople}>
            <People rows={(props.personList?.people || []).map(s => ({ data: s }))}
              loading={props.personList?.isFetching || false}
              pagination={props.personList?.pagination || undefined}
              fromIn={true}
            />
          </Collapse>
        </Paper>
        <Paper style={{ borderRadius: "10px", marginTop: "10px", marginBottom: "10px" }} onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); setShowDeliveryLocation(!showDeliveryLocation) }}>
          <ISTableTitle sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }} title={t('deliveryLocations')}>
            <Stack sx={{ display: "flex", flexDirection: "row" }}>
              <PlaceOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} />
              {showDeliveryLocation ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> :
                <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} />}
            </Stack>
          </ISTableTitle>
          <Collapse style={{ padding: "10px" }} in={showDeliveryLocation}>
          {
            <DeliveryLocations
              rows={(props.deliveryLocationList.deliveryLocations || []).map(s => ({ data: s }))}
              loading={props.deliveryLocationList.isFetching}
              provinceList={props.provinceList}
              pagination={props.deliveryLocationList.pagination || undefined}
              fromIn={true}
            />}
          </Collapse>
        </Paper>
      </Collapse>
    </Paper>

  )
}
